import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getInBody, type InBodyTeaser } from "../lib/api";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "../data/links";
import ScoreRing from "../components/ScoreRing";
import { useT } from "../i18n";
import type { Lang } from "../i18n";
import { errorMessage } from "../i18n/errors";

function has(v?: number): v is number {
  return v !== undefined && v !== null && !Number.isNaN(v);
}

function fmt(v?: number, unit?: string): string {
  if (!has(v)) return "—";
  const rounded = Math.round(v * 10) / 10;
  return unit ? `${rounded} ${unit}` : String(rounded);
}

// BCP-47 locale per app language for date formatting.
const LOCALE: Record<Lang, string> = { kk: "kk-KZ", ru: "ru-RU", en: "en-US" };

function fmtDate(iso: string | undefined, lang: Lang): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(LOCALE[lang], { day: "numeric", month: "long" }).format(d);
}

export default function InBody() {
  const nav = useNavigate();
  const { t, lang } = useT();
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";

  const [data, setData] = useState<InBodyTeaser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!phone) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    getInBody()
      .then((r) => {
        if (alive) setData(r);
      })
      .catch((e: any) => {
        if (alive) setError(errorMessage(e, t));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [phone, t]);

  const testDate = fmtDate(data?.testDate, lang);

  return (
    <div className="min-h-dvh flex flex-col px-[22px] py-8 max-w-md mx-auto">
      <header className="mb-6 flex items-center gap-3">
        <button
          type="button"
          onClick={() => nav(-1)}
          aria-label={t("inbody.back")}
          className="w-9 h-9 rounded-full bg-white/[0.06] flex items-center justify-center active:bg-white/10 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("inbody.title")}</h1>
      </header>

      {loading && <p className="text-muted text-sm">{t("inbody.loading")}</p>}
      {!loading && error && <p className="text-red-400 text-sm">{error}</p>}

      {/* Скачай приложение HJ — полные результаты замера там. Показываем сразу
          при открытии зоны 2, до и после замера. */}
      {!loading && !error && (
        <div className="relative bg-card-2-grad border border-line-3 rounded-[22px] p-5 overflow-hidden mb-4">
          <span
            className="absolute -right-10 -top-10 w-[140px] h-[140px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(124,92,252,0.3), transparent 70%)" }}
          />
          <p className="relative text-sm text-white leading-relaxed">{t("inbody.appPrompt")}</p>
          <div className="relative flex flex-col gap-2.5 mt-4">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full h-12 rounded-[14px] bg-violet-grad text-white font-extrabold text-sm flex items-center justify-center active:opacity-90 transition-opacity shadow-violet-btn"
            >
              {t("done.appStore")}
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full h-12 rounded-[14px] bg-white/[0.08] ring-1 ring-white/15 text-white font-bold text-sm flex items-center justify-center active:bg-white/[0.14] transition-colors"
            >
              {t("done.googlePlay")}
            </a>
          </div>
        </div>
      )}

      {!loading && !error && !data?.hasResult && (
        <div className="bg-card border border-line-2 rounded-[18px] p-5">
          <p className="text-muted text-sm leading-relaxed">{t("inbody.empty")}</p>
        </div>
      )}

      {!loading && !error && data?.hasResult && (
        <div className="flex flex-col gap-4">
          {/* Score teaser */}
          <div className="bg-card border border-line-2 rounded-[18px] p-5 flex flex-col items-center">
            <span className="text-muted text-sm mb-3">{t("inbody.score")}</span>
            <ScoreRing score={data.score ?? 0} size={140} />
            {testDate && <span className="text-muted text-xs mt-4">{t("inbody.measuredAt", { date: testDate })}</span>}
          </div>

          {/* Метаболический возраст — показываем открыто */}
          {data.metabolicAge != null && (
            <div className="bg-card border border-line-2 rounded-[18px] p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/80">{t("inbody.metabolicAge")}</span>
                <span className="text-lg font-semibold">{Math.round(data.metabolicAge)}</span>
              </div>
            </div>
          )}

          {/* Body-fat teaser */}
          <div className="bg-card border border-line-2 rounded-[18px] p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/80">{t("inbody.pbf")}</span>
              <span className="text-lg font-semibold blur-[5px] select-none">{fmt(data.pbf, t("unit.percent"))}</span>
            </div>
          </div>

        </div>
      )}

      {/* Что такое InBody — объясняем метод и зачем знать состав тела */}
      {!loading && !error && (
        <div className="bg-card border border-line-2 rounded-[18px] p-5 mt-4">
          <h2 className="text-[15px] font-extrabold text-white mb-2">{t("inbody.about.title")}</h2>
          <p className="text-[13px] text-muted leading-[1.55]">{t("inbody.about.body")}</p>
        </div>
      )}
    </div>
  );
}
